import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Trash2, FileBox } from "lucide-react";
import { getErrorMessage } from "../../utils/errorHandler";
import { API, authHeader, sendSermonToEditor } from "./shared";

export default function SermonLibraryPanel({ t, refreshKey, active }) {
  const navigate = useNavigate();
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/pulpit/sermons`, { headers: authHeader() });
      setSermons(res.data.sermons || []);
    } catch (err) {
      toast.error(getErrorMessage(err, t("load_failed") || "Load failed"));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (active === undefined || active) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, active]);

  const remove = async (sermon_id, e) => {
    e.stopPropagation();
    if (!window.confirm(t("confirm_delete_sermon") || "Delete this sermon?")) return;
    try {
      await axios.delete(`${API}/api/pulpit/sermons/${sermon_id}`, { headers: authHeader() });
      setSermons((s) => s.filter((x) => x.sermon_id !== sermon_id));
      if (selected?.sermon_id === sermon_id) setSelected(null);
      toast.success(t("sermon_deleted") || "Deleted");
    } catch (err) {
      toast.error(getErrorMessage(err, t("delete_failed") || "Delete failed"));
    }
  };

  const sendToEditor = () => {
    if (!selected) return;
    sendSermonToEditor({
      title: selected.title,
      sermon_type: selected.sermon_type,
      content: selected.content,
      navigate, t,
    });
  };

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-4">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t("my_sermons") || "My Sermons"}</CardTitle>
          <CardDescription className="text-xs">{sermons.length} {t("saved") || "saved"}</CardDescription>
        </CardHeader>
        <CardContent className="px-2">
          <ScrollArea className="h-[500px]">
            {loading ? (
              <div className="text-center py-6"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
            ) : sermons.length === 0 ? (
              <div className="text-xs text-slate-500 text-center py-6">{t("no_saved_sermons") || "No saved sermons yet."}</div>
            ) : (
              <div className="space-y-1">
                {sermons.map((s) => (
                  <div key={s.sermon_id} onClick={() => setSelected(s)}
                    className={`p-2 rounded cursor-pointer text-xs flex items-start justify-between gap-1 hover:bg-amber-500/10 ${selected?.sermon_id === s.sermon_id ? "bg-amber-500/10 border border-amber-500/30" : ""}`}
                    data-testid={`sermon-card-${s.sermon_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate text-slate-200">{s.title}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                        <Badge variant="outline" className="text-[9px] px-1 py-0">{s.sermon_type}</Badge>
                        {s.scripture && <span className="truncate">{s.scripture}</span>}
                      </div>
                    </div>
                    <button onClick={(e) => remove(s.sermon_id, e)}
                      className="opacity-50 hover:opacity-100 hover:text-red-400 transition-opacity"
                      data-testid={`delete-sermon-${s.sermon_id}`} aria-label="Delete">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">{selected?.title || (t("preview") || "Preview")}</CardTitle>
            <CardDescription>{selected?.scripture || (t("select_sermon_to_view") || "Select a sermon to view")}</CardDescription>
          </div>
          {selected && (
            <Button variant="outline" size="sm" onClick={sendToEditor} data-testid="send-library-to-editor-btn" className="text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10">
              <FileBox className="w-3.5 h-3.5 mr-1" />
              {t("send_to_editor") || "Send to Editor"}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[450px] pr-2">
            {!selected ? (
              <div className="text-sm text-slate-500 text-center py-10">
                {t("select_from_left") || "Pick a sermon from the left panel."}
              </div>
            ) : (
              <pre className="text-xs text-slate-200 whitespace-pre-wrap" data-testid="sermon-detail">
                {JSON.stringify(selected.content, null, 2)}
              </pre>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
